/**
 * Whether a document-level pointer event happened outside `element`.
 *
 * `event.target` is not usable for this once the viewer renders inside a shadow root: the
 * DOM retargets it to the shadow host before a listener on `document` sees it, so a click
 * on a menu item reports the host element and every "click outside" check says true —
 * menus close the instant you press a button inside them. `composedPath()` is the path
 * before retargeting, so it still contains the element that was really clicked.
 */
export function isEventOutsideElement(event: Event, element: Element | null): boolean {
    if (!element) {
        return true;
    }
    if (typeof event.composedPath === 'function') {
        const path = event.composedPath();
        if (path.length > 0) {
            return !path.includes(element);
        }
    }
    // Older environments (and jsdom without composedPath) fall back to the plain target,
    // which is correct whenever no shadow boundary is involved.
    const target = event.target;
    return !(target instanceof Node) || !element.contains(target);
}
